import { FC, useMemo } from "react";
import { SxProps } from "@mui/material";
import { MonthItem } from "./MonthItem";
import { Month } from "./Month";
import { merge } from "lodash";
import { TimeframeProps } from "shared-ts-ui-timeframe";
import { Button_color } from "shared-ts-mui";
import { Flue } from "firefly-my-chimney-ts-building";
import { flueBurnMetricAggregateTimeUnits } from "firefly-shared-ts-domain";
import { useHotkeys } from "react-hotkeys-hook";
import { Key } from "ts-key-enum";
import { DateTime } from "luxon";
import { TimeWheel, useDateTimeNow } from "shared-ts-utils-luxon";
import { ScrollMenu, ScrollMenuItem } from "shared-ts-scroll-menu";
import { notUndef } from "shared-ts-utils-core";

export interface PeriodSelectProps {
  timeframe: TimeframeProps;
  flue: Flue;
  color?: Button_color;
  sx?: SxProps;
}

export const PeriodSelect: FC<PeriodSelectProps> = (props) => {
  const { flue } = props;
  const now = useDateTimeNow();

  const months = useMemo(() => {
    const monthsWithMetrics = flue.metrics
      .filter((it) => it.timeUnit === flueBurnMetricAggregateTimeUnits.month)
      .map((it) => {
        return new Month({ dateTime: it.time as DateTime<true> });
      })
      .sort((a, b) => a.dateTime.toMillis() - b.dateTime.toMillis());

    const firstMetricTime = notUndef(monthsWithMetrics[0], (it) => it.dateTime);

    const allMonths: Array<Month> = [];

    if (firstMetricTime !== undefined) {
      new TimeWheel({ start: firstMetricTime, timeUnit: "month" }).runUntilTime(
        now.toUTC(),
        (dateTime) => {
          const month = monthsWithMetrics.find((month) =>
            month.dateTime.equals(dateTime)
          );
          if (month) {
            allMonths.push(month);
          } else {
            const missingMonth = new Month({ dateTime: dateTime });
            allMonths.push(missingMonth);
          }
        }
      );
    }
    return allMonths.reverse();
  }, [flue, now]);

  useHotkeys(Key.ArrowLeft, () => {
    props.timeframe.onNextTimeframe();
  });

  useHotkeys(Key.ArrowRight, () => {
    props.timeframe.onPreviousTimeframe();
  });

  const sx = useMemo(
    () =>
      merge({}, props.sx, {
        overflowX: "auto",
        scrollbarWidth: "none",
        overflowY: "visible",
        position: "relative",
        "> .MuiButton-root": {
          marginBottom: "5px",
          marginRight: "5px",
          marginLeft: "2px",
          marginTop: "2px",
        },
      }),
    [props.sx]
  );

  const handleMonthSelect = (value: Month) => {
    props.timeframe.onTimeframeChange(value.toTimeframeObject());
  };

  const selectedMonth = useMemo(() => {
    let foundMonth = months.find((it) =>
      it.dateTime.equals(props.timeframe.timeframe.start)
    );

    if (foundMonth === undefined && months.length > 0) {
      foundMonth = months.reduce((nearest, current) => {
        const currentDiff = Math.abs(
          current.dateTime
            .diff(props.timeframe.timeframe.start)
            .as("milliseconds")
        );
        const nearestDiff = Math.abs(
          nearest.dateTime
            .diff(props.timeframe.timeframe.start)
            .as("milliseconds")
        );
        return currentDiff < nearestDiff ? current : nearest;
      }, months[0]);
    }
    return foundMonth;
  }, [months, props.timeframe.timeframe.start]);

  return (
    <ScrollMenu gap={1} sx={sx} scrollToItem={selectedMonth?.id}>
      {months.map((month, index) => {
        const selected = month === selectedMonth;

        const metrics = flue.querySingleMetricsOrUndef(
          flueBurnMetricAggregateTimeUnits.month,
          month.dateTime
        );

        return (
          <ScrollMenuItem itemKey={month.id} key={month.id}>
            <MonthItem
              month={month}
              selected={selected}
              metrics={metrics}
              color={props.color}
              onSelect={handleMonthSelect}
            />
          </ScrollMenuItem>
        );
      })}
    </ScrollMenu>
  );
};
